import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Flex, HStack, Skeleton, Spinner, Text, VStack } from "@chakra-ui/react";
import { HiChevronDown, HiOutlineCpuChip } from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { ModelService } from "@/services/ModelService";
import { CampaignService } from "@/services/CampaignService";
import { AgentAvatar } from "@/pages/models/components/AgentCatalogCard";
import { catalogEntryForType } from "@/pages/models/agentCatalog";
import { RecruitmentStatusBadge } from "@/pages/models/RecruitmentStatusBadge";
import { CampaignStatusBadge } from "@/pages/models/components/CampaignStatusBadge";

import i18n from "i18next";

const formatDate = (value) => (value ? new Date(value).toLocaleDateString(i18n.language) : "-");

const ItemRow = ({ title, badge, date, onClick }) => (
    <HStack
        w="full"
        px={4}
        py={3}
        gap={3}
        cursor="pointer"
        borderTop="1px solid"
        borderColor="gray.100"
        _hover={{ bg: "gray.50" }}
        transition="background 0.15s"
        onClick={onClick}
    >
        <Text flex={1} minW={0} truncate fontSize="sm" fontWeight={500} color="gray.900">{title}</Text>
        {badge}
        <Text fontSize="xs" color="gray.500" w="80px" textAlign="right" flexShrink={0}>{date}</Text>
    </HStack>
);

// `items` is undefined while loading, null on error.
const ScenarioItems = ({ agentId, items, isSales }) => {
    const { t } = useTranslation("models");
    const navigate = useNavigate();

    if (items === undefined) {
        return (
            <HStack px={4} py={4} justify="center"><Spinner size="sm" color="purple.500" /></HStack>
        );
    }
    if (items === null) return <Text px={4} py={3} fontSize="sm" color="red.500">{t("modelDetailPage.loadError")}</Text>;
    if (items.length === 0) {
        return (
            <Text px={4} py={3} fontSize="sm" color="gray.500">
                {t(isSales ? "modelDetailPage.noCampaigns" : "modelDetailPage.noRecruitments")}
            </Text>
        );
    }

    return items.map((item) =>
        isSales ? (
            <ItemRow
                key={item.id}
                title={item.name}
                badge={<CampaignStatusBadge status={item.status} />}
                date={formatDate(item.createdAtUtc)}
                onClick={() => navigate(`/models/${agentId}/campaigns/${item.id}/leads`)}
            />
        ) : (
            <ItemRow
                key={item.id}
                title={item.jobTitle}
                badge={<RecruitmentStatusBadge status={item.status} />}
                date={formatDate(item.createdAtUtc)}
                onClick={() => navigate(`/models/${agentId}/recruitments/${item.id}/candidates`)}
            />
        )
    );
};

const ScenarioCard = ({ agentId, scenario, items, isSales, expanded, onToggle }) => {
    const { t } = useTranslation("models");
    const navigate = useNavigate();

    return (
        <Box
            w="full"
            bg="white"
            border="1px solid"
            borderColor={expanded ? "purple.200" : "gray.200"}
            borderRadius={12}
            overflow="hidden"
            transition="border-color 0.2s, box-shadow 0.2s"
            boxShadow={expanded ? "0 4px 16px rgba(23,21,31,0.06)" : "none"}
        >
            <HStack px={4} py={4} gap={3} cursor="pointer" onClick={onToggle} _hover={{ bg: "gray.50" }} transition="background 0.15s">
                <Box
                    color="gray.500"
                    flexShrink={0}
                    transition="transform 0.2s"
                    transform={expanded ? "rotate(180deg)" : "rotate(0deg)"}
                >
                    <HiChevronDown />
                </Box>
                <VStack align="flex-start" gap={0.5} flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight={600} color="gray.900" truncate maxW="full">{scenario.name}</Text>
                    <Text fontSize="xs" color="gray.500">
                        {t("modelDetailPage.edited")}: {formatDate(scenario.editedAtUtc)} · {t("modelDetailPage.created")}: {formatDate(scenario.createdAtUtc)}
                    </Text>
                </VStack>
                <Button
                    variant="outlined"
                    size="sm"
                    flexShrink={0}
                    onClick={(e) => { e.stopPropagation(); navigate(`/models/${agentId}/scenarios/${scenario.id}`); }}
                >
                    {t("modelDetailPage.openScenario")}
                </Button>
            </HStack>
            <Box
                display="grid"
                gridTemplateRows={expanded ? "1fr" : "0fr"}
                transition="grid-template-rows 0.28s cubic-bezier(0.32, 0.72, 0, 1)"
            >
                <Box overflow="hidden" minH={0}>
                    <Box
                        bg="gray.50"
                        borderTop="1px solid"
                        borderColor="gray.200"
                        opacity={expanded ? 1 : 0}
                        transition="opacity 0.2s"
                    >
                        <Text px={4} pt={3} pb={2} fontSize="xs" fontWeight={600} color="gray.500" textTransform="uppercase" letterSpacing="wider">
                            {t(isSales ? "modelDetailPage.campaignsTitle" : "modelDetailPage.recruitmentsTitle")}
                        </Text>
                        <Box bg="white">
                            <ScenarioItems agentId={agentId} items={items} isSales={isSales} />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const ModelDetailPage = () => {
    const { t } = useTranslation("models");
    const { id } = useParams();

    const [agent, setAgent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [itemsByScenario, setItemsByScenario] = useState({});

    useSetPageHeader(t("modelDetailPage.headerTitle"), "", <HiOutlineCpuChip />);

    useEffect(() => {
        setIsLoading(true);
        setItemsByScenario({});
        ModelService.getById(id).then((data) => {
            setAgent(data);
            setIsLoading(false);
            const isSales = data?.type === "SalesManager";
            (data?.scenarios ?? []).forEach((scenario) => {
                const request = isSales ? CampaignService.getByScenario(scenario.id) : ModelService.getRecruitments(scenario.id);
                request
                    .then((res) => res?.items ?? [])
                    .catch(() => null)
                    .then((items) => setItemsByScenario((prev) => ({ ...prev, [scenario.id]: items })));
            });
        });
    }, [id]);

    if (isLoading) {
        return (
            <Flex w="full" py={6} gap={6} align="flex-start" direction={{ base: "column", lg: "row" }}>
                <Skeleton w={{ base: "full", lg: "280px" }} height="240px" borderRadius={16} flexShrink={0} />
                <VStack flex={1} w="full" align="stretch" gap={3}>
                    {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height="64px" borderRadius={12} />)}
                </VStack>
            </Flex>
        );
    }

    if (!agent) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="gray.500">{t("modelDetailPage.notFound")}</Text>
            </VStack>
        );
    }

    const scenarios = agent.scenarios ?? [];
    const isSales = agent.type === "SalesManager";
    const entry = catalogEntryForType(agent.type);

    return (
        <Flex w="full" py={6} gap={6} align="flex-start" direction={{ base: "column", lg: "row" }}>
            <VStack
                w={{ base: "full", lg: "280px" }}
                flexShrink={0}
                align="flex-start"
                gap={4}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius={16}
                p={5}
                pt={7}
                position={{ lg: "sticky" }}
                top={{ lg: 6 }}
            >
                <AgentAvatar image={entry.image} tile={entry.tile} size={88} />
                <VStack align="flex-start" gap={1.5}>
                    <Text fontWeight={700} fontSize="lg" color="gray.900" lineHeight={1.25}>{agent.name}</Text>
                    {agent.description && (
                        <Text color="gray.500" fontSize="sm" lineHeight={1.6}>{agent.description}</Text>
                    )}
                </VStack>
            </VStack>

            <VStack flex={1} minW={0} w="full" align="stretch" gap={3}>
                <Text fontWeight={700} fontSize="md" color="gray.900">{t("modelDetailPage.scenariosListTitle")}</Text>
                {scenarios.map((scenario) => (
                    <ScenarioCard
                        key={scenario.id}
                        agentId={id}
                        scenario={scenario}
                        items={itemsByScenario[scenario.id]}
                        isSales={isSales}
                        expanded={expandedId === scenario.id}
                        onToggle={() => setExpandedId(expandedId === scenario.id ? null : scenario.id)}
                    />
                ))}
            </VStack>
        </Flex>
    );
};

export default ModelDetailPage;
